import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classes from './style.module.scss';
import Modal from '~/app/common/Modal';
import SelectedPartList from './SelectedPartList';
import { useStoreActions, useStoreState } from '~/store';
import Input from '~/app/modules/form/Search/Input';
import ManualPartForm from './ManualPartForm';
import Checkout from './Checkout';
import Icon from '~/app/common/Icon';
import useTranslation from '~/app/hooks/useTranslation';
import { Part } from '~/types';
import { DEPARTMENT_PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import useAccessControl from '~/app/hooks/useAccessControl';
import { PartLinkTypes } from '~/app/data/statuses/issue';
import ConfirmationMenu from '~/app/common/ConfirmationMenu';
import { getPartStoreKey, isTempPartsDirty } from '../helper';

export interface AddPartDialogProps {
  open: boolean
  onClose: () => void
  type: PartLinkTypes
}

export type AddPartDialogComponent = React.FC<AddPartDialogProps>;

const AddPartDialog: AddPartDialogComponent = ({ open, onClose, type }) => {

  const basePath = getPartStoreKey(type);
  const deptPrefix = useStoreState(state => state[basePath].data.departmentPrefix);
  const parts = useStoreState(state => state[basePath].data.parts ?? []);
  const tempParts = useStoreState(state => state[basePath].tempParts ?? []);
  const partUpdating = useStoreState(state => state[basePath].partUpdating ?? []);
  const searchPartAction = useStoreActions(actions => actions[basePath].fetchSearchParts ?? []);

  const [isOpenCheckoutPage, setIsOpenCheckoutPage] = useState<boolean>(false);
  const [isOpenManualPartForm, setIsOpenManualPartForm] = useState<boolean>(false);
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [partForEdit, setPartForEdit] = useState<Part>();

  const { t } = useTranslation('parts');
  const { checkPermission } = useAccessControl();

  const addManualPartsPermission = deptPrefix
    ? checkPermission(DEPARTMENT_PERMISSION_GROUPS.addManualParts(deptPrefix))
    : false;

  const visibleParts = useMemo(
    () => tempParts.filter(dM => !dM.deleted),
    [tempParts]
  );
  const uniqueCount = visibleParts.length;
  const totalQuantity = visibleParts.reduce((sum, part) => sum + part.quantity, 0);

  const searchPart = useCallback((searchText: string) => {
    setSearchText(searchText);
    searchPartAction({ searchText, offset: 0 });
  }, [searchPartAction]);

  const forceClose = useCallback(() => {
    setSearchText('');
    setIsOpenCheckoutPage(false);
    setIsOpenManualPartForm(false);
    setIsDiscardConfirmOpen(false);
    setPartForEdit(undefined);
    onClose();
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (isTempPartsDirty(tempParts, parts)) {
      setIsDiscardConfirmOpen(true);
      return;
    }
    forceClose();
  }, [tempParts, parts, forceClose]);

  const editManualPart = useCallback((part: Part) => {
    setPartForEdit(part);
    setIsOpenManualPartForm(true);
  }, [setPartForEdit, setIsOpenManualPartForm]);

  useEffect(() => {
    if (open) {
      searchPartAction({ searchText: '', offset: 0 });
    }
  }, [searchPartAction, open]);

  const summaryLabel = uniqueCount === 1
    ? t('reviewSummarySingular', { count: uniqueCount, qty: totalQuantity })
    : t('reviewSummaryPlural', { count: uniqueCount, qty: totalQuantity });

  return (
    <Modal className={classes['addPartDialog']} open={open} onClose={requestClose} position={'bottom'}>
      <div className={classes['partManager']}>
        <div className={classes['partsHeader']}>
          <div className={classes['partsSubHeader']}>
            <Icon name='arrow_back' onClick={requestClose}/>
            <span>{t('add')} {t('parts')}</span>
          </div>
        </div>
        <Input
          autoFocus={false}
          placeholder={t('search')}
          className={classes["searchInput"]}
          inputClassName={classes["input"]}
          onSearch={searchPart}
          fetching={partUpdating}
          value={searchText}
          iconPosition='left'
        />
        <SelectedPartList searchText={searchText} editManualPart={editManualPart} type={type}/>
        {addManualPartsPermission === true && (
          <button className={classes['manualAddButton']} onClick={() => { setPartForEdit(undefined); setIsOpenManualPartForm(true); }}>
            + {t('addPartManually')}
          </button>
        )}
        <div className={classes['reviewFooter']}>
          <span className={classes['reviewSummary']}>{summaryLabel}</span>
          <button
            type="button"
            className={classes['reviewButton']}
            onClick={() => setIsOpenCheckoutPage(true)}
            disabled={uniqueCount === 0}
            aria-disabled={uniqueCount === 0}
          >
            {t('review')}
            <Icon name="keyboard_arrow_down" size={18} />
          </button>
        </div>
      </div>
      <ManualPartForm open={isOpenManualPartForm} onClose={() => {setIsOpenManualPartForm(false); setPartForEdit(undefined);}} part={partForEdit} type={type} />
      <Checkout open={isOpenCheckoutPage} onClose={() => setIsOpenCheckoutPage(false)} onCloseAll={forceClose} type={type} />
      <ConfirmationMenu
        isOpen={isDiscardConfirmOpen}
        onConfirm={forceClose}
        onCancel={() => setIsDiscardConfirmOpen(false)}
        confirmationText={t('discardConfirm')}
        confirmLabel={t('discard')}
        cancelLabel={t('keepEditing')}
      />
    </Modal>
  );
};

export default AddPartDialog;
